import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { DateTime } from "luxon";
import { useContext, useEffect, useRef, useState } from "react";

import { HandledRequestError } from "../Components/ErrorHandling/ErrorHandler";
import { SessionContext } from "../Context/SessionContext";
import { UserContext } from "../Context/UserContext";
import { GemError } from "../Models/GemError";
import EnvironmentConfiguration from "../V2/Constants/EnvironmentConfiguration";
import { Error, ErrorBehaviors, dismissError } from "./Errors";

const cancellationMessage = "User canceled the request by issuing an updated one.";
const tokenRevokedErrorMessage = "The request authorization was revoked.";

export type RequestMethod = "get" | "post" | "put" | "delete";
export interface UseApiProps<TData> {
  method?: RequestMethod;
  methodRef?: React.MutableRefObject<RequestMethod>;
  url?: string;
  urlRef?: React.MutableRefObject<string>;
  data?: TData;
  dataRef?: React.MutableRefObject<TData>;
  config?: AxiosRequestConfig;
  configRef?: React.MutableRefObject<AxiosRequestConfig | undefined>;
  valid?: boolean;
  isCancelable?: boolean;
  renewsToken?: boolean;
  error?: Error;
  errorRef?: React.MutableRefObject<Error>;
}

/** @deprecated Do not continue feature development of this deprecated hook. Instead use `UseGem`. */
const buildUseApiHook =
  (axiosInstance: AxiosInstance) =>
  <TResponse, TData = undefined>({
    method,
    methodRef,
    url,
    urlRef,
    data,
    dataRef,
    config,
    configRef,
    valid = true,
    isCancelable,
    renewsToken = true,
    error: errorKey,
    errorRef: errorKeyRef
  }: UseApiProps<TData>): {
    response?: TResponse;
    error?: AxiosError<GemError>;
    isLoading: boolean;
  } => {
    const {
      showReauthenticator,
      token,
      setShowReauthenticator,
      setHandledError: setUserError,
      setLogOutProtocol,
      setShouldRenewToken
    } = useContext(UserContext);
    const { helpers: { addRequestError = undefined, setHandledError: setSessionError = undefined } = {} } =
      useContext(SessionContext);

    const [error, setError] = useState<AxiosError<GemError>>();
    const [isLoading, setLoading] = useState(false);
    const [isRetry, setIsRetry] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [response, setResponse] = useState<TResponse>();

    const setErrorRef = useRef<(error: HandledRequestError) => void>();
    const [isSuspended, setIsSuspended] = useState(false);
    const cancelRef = useRef(axios.CancelToken.source());
    const loadingRef = useRef(isLoading);
    const errorResponseRef = useRef<AxiosError<GemError>>();

    useEffect(() => {
      loadingRef.current = isLoading;
    }, [isLoading]);
    const tokenRef = useRef(token);
    useEffect(() => {
      tokenRef.current = token;

      // Only repeat if we are not suspending for Auth Modal.
      if (!showReauthenticator) setIsSuspended(false);
    }, [token, showReauthenticator]);
    useEffect(() => {
      if (setSessionError) setErrorRef.current = setSessionError;
      else if (setUserError) setErrorRef.current = setUserError;
    }, [setUserError, setSessionError]);

    // stringify complex inputs for simple deep equivalency check
    const dataString = data ? JSON.stringify(data) : undefined;
    const configString = config ? JSON.stringify(config) : "";

    // If this gets dismissed and we have a stored error we need to do the things the catch would have done. BWP.
    useEffect(() => {
      if (!isDismissed) return;

      const requestError = errorKeyRef?.current || errorKey;

      if (errorResponseRef.current) {
        if (addRequestError) addRequestError(errorResponseRef.current);
        setError(errorResponseRef.current);
        errorResponseRef.current = undefined;
      }

      if (requestError) dismissError(requestError);
      setIsDismissed(false);
    }, [isDismissed, addRequestError, errorKey, errorKeyRef]);

    useEffect(() => {
      if (!valid || isSuspended) return;

      // setup request config
      const config: AxiosRequestConfig = (configString && JSON.parse(configString)) || configRef?.current || {};
      config.headers = config.headers || {};
      const requestUrl = url || urlRef?.current || "";

      // IF the request url IS NOT token AND has no manual token provided
      if (
        !requestUrl.startsWith("token") &&
        !requestUrl.startsWith("Legacy/V0/Token") &&
        !requestUrl.startsWith("Legacy/V1/Token") &&
        !config.headers.Authorization
      ) {
        // validate the token
        if (tokenRef.current) {
          const nowUtc = DateTime.utc();
          // IF renewal is expired, log out
          if (DateTime.fromISO(tokenRef.current.refreshExpires).toUTC() < nowUtc) {
            return setLogOutProtocol && setLogOutProtocol("expired");
          }

          // ELSE renew it IF NOT disabled AND token expires within 5 mins
          if (renewsToken && DateTime.fromISO(tokenRef.current.tokenExpires).diff(nowUtc, "minutes").minutes < 5) {
            setIsSuspended(true);
            setShouldRenewToken(true);
            return;
          }
        }
        // ELSE token is missing, log out
        // TODO: Remove need for null check
        else {
          return setLogOutProtocol && setLogOutProtocol("expired");
        }
        config.headers.Authorization = `Bearer ${tokenRef.current.token}`;
      }
      const data = dataString ? JSON.parse(dataString) : dataRef?.current;
      const requestMethod = method || methodRef?.current || "get";
      // if this request is pending and cancelable and, cancel it
      if (loadingRef.current && (isCancelable || (isCancelable === undefined && requestMethod === "get"))) {
        cancelRef.current.cancel(cancellationMessage);
        cancelRef.current = axios.CancelToken.source();
      }
      config.cancelToken = cancelRef.current?.token;
      const requestError = errorKeyRef?.current || errorKey;

      // build the request
      const fetchData = async () => {
        const request =
          requestMethod === "get"
            ? axiosInstance.get(requestUrl, config)
            : requestMethod === "post"
            ? axiosInstance.post(requestUrl, data, config)
            : requestMethod === "put"
            ? axiosInstance.put(requestUrl, data, config)
            : axiosInstance.delete(requestUrl, { ...config, data });
        request
          .then(({ data }: AxiosResponse<TResponse>) => {
            setResponse(data || ({} as TResponse));
            if (requestError) dismissError(requestError);
          })
          .catch((error: AxiosError<GemError>) => {
            // if the request was cancelled, swallow the error
            if (error.message === cancellationMessage) return;

            let shouldSetError = true;

            // if a 401 error and the the token was revoked, reauthenticate the user
            if (
              error?.response?.status === 401 &&
              (error?.response?.data.data || error?.response?.data.description === tokenRevokedErrorMessage)
            ) {
              setIsSuspended(true);
              setShowReauthenticator(true);
              return;
            }

            // if error handling is enabled for this request
            if (requestError) {
              // if failure to connect to server
              let behavior =
                error.message === "Network Error" ? ErrorBehaviors[Error.UncaughtNetwork]["default"] : undefined;

              // else try to get the exact status behavior from the config
              if (behavior === undefined)
                behavior = error?.response?.status ? ErrorBehaviors[requestError][error.response.status] : undefined;

              // if a 401 error and not explicitly handled and not an auth request, swallow the error
              if (
                behavior === undefined &&
                error?.response?.status === 401 &&
                !(error?.config?.url || "").includes("token")
              )
                return;

              // finally use the error's default behavior
              if (behavior === undefined) behavior = ErrorBehaviors[requestError]["default"];

              // execute the error behavior if we found one
              if (setErrorRef.current && behavior) {
                // Possibly not ideal, but works. BWP
                if (behavior.actionBtnTitle === "Retry") shouldSetError = false;

                setErrorRef.current({
                  key: requestError,
                  behavior: { ...behavior },
                  requestError: error,
                  retry: setIsRetry,
                  dismiss: setIsDismissed
                });
              }
            }

            // save the error to the session
            if (addRequestError && shouldSetError) addRequestError(error);

            // update error state for hook listeners
            if (shouldSetError) setError(error);
            else errorResponseRef.current = error;
          })
          .finally(() => setLoading(false));
      };
      fetchData();
      setLoading(true);
    }, [
      configString,
      configRef,
      dataString,
      dataRef,
      errorKey,
      errorKeyRef,
      isCancelable,
      isRetry,
      isSuspended,
      method,
      methodRef,
      renewsToken,
      url,
      urlRef,
      valid,
      addRequestError,
      setShowReauthenticator,
      setLogOutProtocol,
      setShouldRenewToken
    ]);

    return { response, error, isLoading };
  };

/** @deprecated Do not add new instances of use of this hook in new requests. Instead use `UseGem`. */
export const useApi = buildUseApiHook(axios.create({ baseURL: EnvironmentConfiguration.apiBaseUrl }));
